// Pure mapping helpers between a catalog component entry and Sandpack files.
// No React or MUI imports on purpose: this module is unit-testable in isolation
// and is the single place that knows the catalog component shape.

export const APP_ENTRY = '/App.js';

/**
 * Convert the producer's `publicDeps` into the dependency map Sandpack expects.
 *
 * The real catalog (zippedi-tools-js v1) ships `publicDeps` as an array of bare
 * package names WITHOUT versions, so we use 'latest'. react / react-dom are dropped
 * on purpose: LibraryPlayground pins the sandbox to React 18 (the documented libs are
 * React 18-era) and letting 'latest' through would override that pin.
 */
export function publicDepsToDependencies(publicDeps) {
  const dependencies = {};
  publicDeps.forEach(name => {
    if (name === 'react' || name === 'react-dom') return;
    dependencies[name] = 'latest';
  });
  return dependencies;
}

// Inject well-known transitive peer deps the catalog's publicDeps may omit (they are
// hoisted/transitive in the producer app but the sandbox resolves only what is declared).
// Safety net for the consumer; the producer should ideally ship a self-sufficient
// publicDeps. Returns a NEW object (never mutates the catalog).
export function augmentPeerDependencies(dependencies) {
  const deps = { ...dependencies };
  const names = Object.keys(deps);
  const ensure = name => {
    if (!deps[name]) deps[name] = 'latest';
  };

  const usesMui = names.some(name => name.startsWith('@mui/'));
  const usesMuiX = names.some(name => name.startsWith('@mui/x-'));

  // MUI styled-engine needs Emotion; @mui/x-* (data-grid, date-pickers) needs @mui/material.
  if (usesMui || usesMuiX) {
    ensure('@emotion/react');
    ensure('@emotion/styled');
  }
  if (usesMuiX) ensure('@mui/material');
  // @mui/x-date-pickers v6 + AdapterDateFns requires date-fns v2 (v3 throws
  // "package v3.x is not compatible with this adapter"). FORCE ^2 even if the catalog ships
  // date-fns at 'latest'/v3 (the producer's publicDeps version mismatches the v6 adapter).
  // (Revisit if the catalog bumps x-date-pickers to v7+, which supports date-fns v3.)
  if (deps['@mui/x-date-pickers']) deps['date-fns'] = '^2.30.0';
  // react-i18next needs an i18next instance (the harness initializes a no-op one).
  if (deps['react-i18next']) ensure('i18next');

  // The @nivo/* stack declares several PEER deps that Sandpack does not auto-install
  // (it resolves regular deps transitively, but not peers): @nivo/core (peer of each chart
  // package) and prop-types (peer of @nivo/core). Inject both. @nivo/core takes the same
  // version as the chart package; prop-types is version-stable.
  const nivoDep = names.find(n => n.startsWith('@nivo/') && n !== '@nivo/core');
  if (nivoDep) {
    if (!deps['@nivo/core']) deps['@nivo/core'] = deps[nivoDep] || '^0.80.0';
    if (!deps['prop-types']) deps['prop-types'] = 'latest';
  }

  return deps;
}

/**
 * Normalize a raw catalog component into the canonical shape used across the UI.
 *
 * The producer ships FLAT fields: `entry` (absolute /lib/ path), `files` (map of
 * /lib/ path -> code) and `publicDeps` (array of package names). Older local fixtures
 * used nested `source.{entry,files}` + a `dependencies` map. This accepts either and
 * returns `{ ...raw, entry, files, dependencies }`, where `dependencies` is always a
 * `{name: versionRange}` map.
 */
export function normalizeCatalogComponent(raw) {
  const entry = raw.entry ?? raw.source?.entry;
  const files = raw.files ?? raw.source?.files ?? {};

  // Catalog v2: publicDeps is an OBJECT `{name: versionRange}` with real versions and
  // peer deps already included (the producer resolved F2/F3) — use it directly. The
  // array branch is kept only for backward-compat with older bare-list catalogs.
  let dependencies;
  if (raw.publicDeps && !Array.isArray(raw.publicDeps)) {
    dependencies = raw.publicDeps;
  } else if (Array.isArray(raw.publicDeps)) {
    dependencies = publicDepsToDependencies(raw.publicDeps);
  } else {
    dependencies = raw.dependencies ?? {};
  }

  // Safety net for peer deps the catalog may omit (emotion for MUI, @mui/material for
  // @mui/x-*, date-fns for date-pickers).
  dependencies = augmentPeerDependencies(dependencies);

  return { ...raw, entry, files, dependencies };
}

/**
 * Generate a copy-paste usage skeleton for a catalog component.
 *
 * Produces: import line + JSX snippet with example props (when the catalog ships
 * them) or required props as type-comment placeholders (when no example exists).
 * Optional props are omitted — the developer discovers them via the Props table.
 */
export function generateUsageSnippet(entry) {
  const name = entry.name || 'Component';
  const localName = entry.localName || name;
  const pkg = entry.package || '';

  const importName = localName !== name ? `${localName} as ${name}` : name;
  const importLine = `import { ${importName} } from '${pkg}';`;

  const props = Array.isArray(entry.props) ? entry.props : [];
  const exampleProps =
    (Array.isArray(entry.examples) && entry.examples[0]?.props) || {};
  const hasExample = Object.keys(exampleProps).length > 0;

  const propLines = [];
  if (hasExample) {
    Object.entries(exampleProps).forEach(([key, value]) => {
      propLines.push(`  ${typeof value === 'string' ? `${key}="${value}"` : `${key}={${JSON.stringify(value)}}`}`);
    });
  } else {
    props
      .filter(p => p.required)
      .forEach(p => propLines.push(`  ${p.name}={/* ${p.type || 'any'} */}`));
  }

  const propsBlock = propLines.length > 0 ? '\n' + propLines.join('\n') + '\n' : '';
  return `${importLine}\n\n<${name}${propsBlock}/>`;
}

// Curated INTERACTIVE demos for known security utilities — a real, runnable usage example
// (shown in the editor for copy, live in the preview) instead of dumping the source. Keyed
// by the utility name within the security package; returns null for anything else so the
// generic value viewer is used. The producer could ship these as runnable `examples` (F4)
// to avoid the consumer hard-coding per utility.
function curatedUtilityDemo(entry, importPath) {
  if (entry.package !== '@zippeditoolsjs/security') return null;
  const local = entry.localName || entry.name;

  if (entry.name === 'generatePassword') {
    return [
      `import { useState } from 'react';`,
      `import { ${local} } from '${importPath}';`,
      '',
      'export default function App() {',
      `  const [password, setPassword] = useState(() => ${local}());`,
      "  return (",
      "    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 16, boxSizing: 'border-box' }}>",
      "    <div style={{ padding: 24, fontFamily: 'sans-serif', maxWidth: 420, width: '100%' }}>",
      "      <h3 style={{ marginTop: 0 }}>generatePassword()</h3>",
      '      <p>Generates a random 8-character password. Click to regenerate.</p>',
      `      <button onClick={() => setPassword(${local}())} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #ccc', cursor: 'pointer' }}>`,
      '        Generate password',
      '      </button>',
      "      <pre style={{ fontSize: 20, background: '#f6f8fa', padding: 16, borderRadius: 8, marginTop: 16 }}>{password}</pre>",
      '    </div>',
      '    </div>',
      '  );',
      '}',
      '',
    ].join('\n');
  }

  if (entry.name === 'Validators') {
    return [
      `import { useState } from 'react';`,
      `import { ${local} } from '${importPath}';`,
      '',
      `const validators = new ${local}(8); // MIN_PASSWORD_LENGTH = 8`,
      '',
      'export default function App() {',
      "  const [value, setValue] = useState('');",
      '  const valid = validators.validatePasswordLength(value);',
      `  const min = validators.MIN_PASSWORD_LENGTH;`,
      '  return (',
      "    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 16, boxSizing: 'border-box' }}>",
      "    <div style={{ padding: 24, fontFamily: 'sans-serif', maxWidth: 420, width: '100%' }}>",
      "      <h3 style={{ marginTop: 0 }}>Validators.validatePasswordLength()</h3>",
      '      <p>A password is valid when it has at least {min} characters.</p>',
      '      <input',
      '        value={value}',
      '        onChange={(e) => setValue(e.target.value)}',
      "        placeholder='Type a password…'",
      "        style={{ padding: 8, width: '100%', boxSizing: 'border-box', borderRadius: 8, border: '1px solid #ccc' }}",
      '      />',
      "      <p style={{ fontWeight: 600, color: !value ? '#666' : valid ? '#15803d' : '#b91c1c' }}>",
      "        {!value ? 'Enter a password' : valid ? '✓ Valid (length ≥ ' + min + ')' : '✗ Too short (min ' + min + ')'}",
      '      </p>',
      '    </div>',
      '    </div>',
      '  );',
      '}',
      '',
    ].join('\n');
  }

  return null;
}

// Tremor v3 styles itself with Tailwind classes (its own `tremor-*` tokens AND plain
// utilities like `w-full h-80` for chart sizing). Without Tailwind, those classes don't apply
// (e.g. `h-80` -> height 0 -> charts render blank). The Sandpack v2 runtime IGNORES a
// `/public/index.html` override, so we inject the Tailwind Play CDN at RUNTIME from the
// generated App.js. Returns the Tailwind theme as JSON (Tremor's design tokens).
function tremorTailwindThemeJSON() {
  const tremorTheme = {
    extend: {
      colors: {
        tremor: {
          brand: { faint: '#eff6ff', muted: '#bfdbfe', subtle: '#60a5fa', DEFAULT: '#3b82f6', emphasis: '#1d4ed8', inverted: '#ffffff' },
          background: { muted: '#f9fafb', subtle: '#f3f4f6', DEFAULT: '#ffffff', emphasis: '#374151' },
          border: { DEFAULT: '#e5e7eb' },
          ring: { DEFAULT: '#e5e7eb' },
          content: { subtle: '#9ca3af', DEFAULT: '#6b7280', emphasis: '#374151', strong: '#111827', inverted: '#ffffff' },
        },
        'dark-tremor': {
          brand: { faint: '#0B1229', muted: '#172554', subtle: '#1e40af', DEFAULT: '#3b82f6', emphasis: '#60a5fa', inverted: '#030712' },
          background: { muted: '#131A2B', subtle: '#1f2937', DEFAULT: '#111827', emphasis: '#d1d5db' },
          border: { DEFAULT: '#1f2937' },
          ring: { DEFAULT: '#1f2937' },
          content: { subtle: '#4b5563', DEFAULT: '#6b7280', emphasis: '#e5e7eb', strong: '#f9fafb', inverted: '#000000' },
        },
      },
      boxShadow: {
        'tremor-input': '0 1px 2px 0 rgb(0 0 0 / 0.05)',
        'tremor-card': '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        'tremor-dropdown': '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      },
      borderRadius: { 'tremor-small': '0.375rem', 'tremor-default': '0.5rem', 'tremor-full': '9999px' },
      fontSize: {
        'tremor-label': '0.75rem',
        'tremor-default': ['0.875rem', { lineHeight: '1.25rem' }],
        'tremor-title': ['1.125rem', { lineHeight: '1.75rem' }],
        'tremor-metric': ['1.875rem', { lineHeight: '2.25rem' }],
      },
    },
  };
  return JSON.stringify(tremorTheme);
}

// Lines that inject the Tailwind Play CDN + Tremor tokens at runtime (Sandpack ignores
// /public/index.html). Reused by the component harness and the showcase.
function tremorTailwindInjectionLines() {
  return [
    `if (typeof document !== 'undefined' && !document.getElementById('__tw_cdn')) {`,
    `  window.tailwind = { config: { theme: ${tremorTailwindThemeJSON()} } };`,
    `  const __twScript = document.createElement('script');`,
    `  __twScript.id = '__tw_cdn';`,
    `  __twScript.src = 'https://cdn.tailwindcss.com';`,
    `  document.head.appendChild(__twScript);`,
    `}`,
  ];
}

// Build a JSX usage string for a showcase member from its example props, e.g.
// `<Badge color="emerald">Active</Badge>` or `<Legend categories={[...]} />`. Used both as
// the rendered element AND the copy snippet.
function showcaseMemberJsx(name, props) {
  const safe = props && typeof props === 'object' ? props : {};
  const { children, ...rest } = safe;
  const attrs = Object.entries(rest)
    .map(([key, value]) =>
      typeof value === 'string' ? ` ${key}="${value}"` : ` ${key}={${JSON.stringify(value)}}`
    )
    .join('');
  if (children === undefined || children === null) return `<${name}${attrs} />`;
  const childStr = typeof children === 'string' ? children : `{${JSON.stringify(children)}}`;
  return `<${name}${attrs}>${childStr}</${name}>`;
}

// Generates the /App.js for a SHOWCASE entry: one compact Sandpack rendering several related
// primitives (e.g. Tremor's Title/Text/Badge…) in `[name | rendered | copy]` rows, instead of
// a giant separate playground per tiny component. Members come from `entry.showcaseMembers`.
function showcaseAppCode(entry, importPath) {
  const members = entry.showcaseMembers || [];
  const importList = members.map(m => `${m.localName} as ${m.name}`).join(', ');

  const rowLines = [];
  members.forEach(member => {
    const examples = Array.isArray(member.examples) && member.examples.length
      ? member.examples
      : [{}];
    examples.forEach(props => {
      const jsx = showcaseMemberJsx(member.name, props);
      rowLines.push(
        `      <Row name=${JSON.stringify(member.name)} snippet={${JSON.stringify(jsx)}}>${jsx}</Row>`
      );
    });
  });

  return [
    `import React, { useState } from 'react';`,
    `import { ${importList} } from '${importPath}';`,
    '',
    ...tremorTailwindInjectionLines(),
    '',
    'function Row({ name, snippet, children }) {',
    '  const [copied, setCopied] = useState(false);',
    '  const copy = () => {',
    '    if (navigator.clipboard) navigator.clipboard.writeText(snippet);',
    '    setCopied(true);',
    '    setTimeout(() => setCopied(false), 1200);',
    '  };',
    '  return (',
    "    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '10px 14px', borderBottom: '1px solid #f0f0f0' }}>",
    "      <code style={{ minWidth: 96, fontSize: 12, color: '#6b7280' }}>{name}</code>",
    "      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>",
    "      <button onClick={copy} title={snippet} style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', background: '#fff', whiteSpace: 'nowrap' }}>",
    "        {copied ? 'Copied' : 'Copy'}",
    '      </button>',
    '    </div>',
    '  );',
    '}',
    '',
    'export default function App() {',
    '  return (',
    "    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', minHeight: '100vh', padding: 16, boxSizing: 'border-box', fontFamily: 'sans-serif' }}>",
    "      <div style={{ width: '100%' }}>",
    ...rowLines,
    '      </div>',
    '    </div>',
    '  );',
    '}',
    '',
  ].join('\n');
}

// Generates the /App.js for a `sandboxType: "utility"` entry — a value viewer that imports
// the real export and visualizes it by shape (color swatches, size bars, shadow boxes, JSON
// for nested objects, source for functions). Tokens have no external deps, so this just
// reads the bundled source.
function utilityViewerAppCode(importPath, candidates, name) {
  return [
    `// Auto-generated token/utility viewer for "${name}".`,
    `import * as Lib from '${importPath}';`,
    '',
    `const value = (${candidates}.map((k) => Lib[k]).find((v) => v !== undefined)) ?? Lib.default;`,
    '',
    `const isColor = (v) => typeof v === 'string' && /^(#|rgb|hsl)/i.test(v.trim());`,
    `const isSize = (v) => typeof v === 'string' && /(px|rem|em|%)$/.test(v.trim()) && !isNaN(parseFloat(v));`,
    `const isShadow = (v) => typeof v === 'string' && v.includes('px') && (v.includes('rgb') || v.includes('#')) && v.trim().split(' ').length >= 3;`,
    '',
    'function Cell({ v }) {',
    "  if (isColor(v)) return <div style={{ width: 28, height: 28, borderRadius: 6, background: v, border: '1px solid rgba(0,0,0,0.15)' }} />;",
    "  if (isShadow(v)) return <div style={{ width: 56, height: 28, borderRadius: 6, background: '#fff', boxShadow: v }} />;",
    "  if (isSize(v)) return <div style={{ height: 12, width: v, maxWidth: '100%', background: '#3b82f6', borderRadius: 3 }} />;",
    "  if (v && typeof v === 'object') return <pre style={{ margin: 0, fontSize: 11 }}>{JSON.stringify(v, null, 2)}</pre>;",
    '  return <span style={{ fontSize: 13 }}>{String(v)}</span>;',
    '}',
    '',
    'function Row({ name, v }) {',
    '  return (',
    "    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>",
    "      <code style={{ minWidth: 150, fontSize: 12 }}>{name}</code>",
    '      <Cell v={v} />',
    "      <code style={{ fontSize: 11, color: '#666', marginLeft: 'auto' }}>{typeof v === 'string' ? v : ''}</code>",
    '    </div>',
    '  );',
    '}',
    '',
    'export default function App() {',
    "  const wrap = (child) => <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 16, boxSizing: 'border-box', fontFamily: 'sans-serif' }}><div style={{ width: '100%', maxWidth: 600 }}>{child}</div></div>;",
    '  if (value === undefined || value === null) return wrap(<em>No value exported.</em>);',
    "  if (typeof value === 'function') {",
    '    return wrap(',
    '      <div>',
    "        <div style={{ marginBottom: 8 }}>ƒ Utility function — see Props for the signature.</div>",
    "        <pre style={{ fontSize: 11, background: '#f6f8fa', padding: 12, borderRadius: 8, overflowX: 'auto' }}>{String(value)}</pre>",
    '      </div>',
    '    );',
    '  }',
    "  if (typeof value === 'object') {",
    '    return wrap(<div>{Object.entries(value).map(([k, v]) => <Row key={k} name={k} v={v} />)}</div>);',
    '  }',
    '  return wrap(<Cell v={value} />);',
    '}',
    '',
  ].join('\n');
}

/**
 * Build the Sandpack `files` map for a (normalized) catalog component entry.
 *
 * `entry.files` are already keyed by their final sandbox paths (e.g.
 * "/lib/nivoRocks/HeatMap.js"), so they are mounted as-is (decision D4: internal
 * source is injected as virtual files). A curated `/App.js` is generated to render
 * the component through a relative import of its entry file.
 *
 * @param {object} entry - Normalized catalog component (see normalizeCatalogComponent).
 * @returns {Object.<string, string>} Sandpack files map keyed by absolute sandbox path.
 */
export function catalogEntryToSandpackFiles(entry) {
  if (!entry) {
    throw new Error('catalogEntryToSandpackFiles: entry is required');
  }

  const files = entry.files || {};
  const entryPath = entry.entry;

  if (!entryPath) {
    throw new Error('catalogEntryToSandpackFiles: entry.entry is required');
  }
  if (Object.keys(files).length === 0) {
    throw new Error('catalogEntryToSandpackFiles: entry.files is empty');
  }

  // entryPath is an absolute sandbox path ("/lib/..."). Import it relative to
  // /App.js: prefixing a dot turns "/lib/x.js" into "./lib/x.js".
  const importPath = entryPath.startsWith('/') ? `.${entryPath}` : `./${entryPath}`;
  const name = entry.name || 'Component';

  // Catalog v2 ships `localName` — the ACTUAL export name in the file (e.g. component
  // "Metric" → "MetricComponent"), which resolves F1. Prefer it, then fall back to
  // `name` / `<name>Component` / default for older catalogs or edge cases. Resolved via a
  // namespace import so any of these export shapes works.
  const candidates = JSON.stringify(
    [entry.localName, name, `${name}Component`].filter(Boolean)
  );

  // sandboxType "utility" = design tokens / helpers, NOT React components (Colors, Spacing,
  // shadows, generatePassword…). Render a value VIEWER that imports the real export and
  // visualizes it by shape (color swatches, size bars, shadow boxes, JSON, function source)
  // instead of the component playground.
  if (entry.sandboxType === 'utility') {
    // Prefer a curated, INTERACTIVE demo for known utilities (a real usage the user can
    // run + copy), falling back to the generic value viewer otherwise.
    const demo = curatedUtilityDemo(entry, importPath);
    return {
      ...files,
      [APP_ENTRY]: demo || utilityViewerAppCode(importPath, candidates, name),
    };
  }

  // Showcase entry: one compact playground rendering several related primitives at once
  // (e.g. Tremor text primitives / badges) instead of a giant box per tiny component.
  if (Array.isArray(entry.showcaseMembers) && entry.showcaseMembers.length > 0) {
    return {
      ...files,
      [APP_ENTRY]: showcaseAppCode(entry, importPath),
    };
  }

  // Props strategy (two buckets, derived from the catalog prop TYPES):
  // - FUNCTION stubs: no-op callbacks for REQUIRED function props only (e.g. `setValue`).
  //   Examples are JSON so they can't carry functions; a required callback that's undefined
  //   crashes when called. OPTIONAL function props are NOT stubbed — they have component
  //   defaults (e.g. BarChart's curried `dataFormatter`), and a naive `() => {}` would clobber
  //   them and break calls like `dataFormatter(metricType)(value)`.
  // - DATA stubs (Array -> [], Object -> {}): applied ONLY when there is no example. They
  //   prevent crashes like `.filter` of undefined, BUT they CLOBBER the component's own
  //   defaults (a default param only applies when the prop is `undefined`; passing `{}`
  //   overrides it). So when a producer example exists, we DON'T apply data stubs — the
  //   example provides the real data and the component's defaults (margins, colors, axes…)
  //   stay intact. This is what made nivo/Tremor charts render blank: empty-object stubs were
  //   overriding their default config.
  const props = Array.isArray(entry.props) ? entry.props : [];
  const fnStubEntries = props
    .filter(prop => String(prop.type || '').toLowerCase().includes('function') && prop.required)
    .map(prop => `${JSON.stringify(prop.name)}: () => {}`);
  const fnStubsCode = `{ ${fnStubEntries.join(', ')} }`;
  // §9.3: do NOT stub Object/Array props — passing `{}`/`[]` overrides component defaultProps
  // and breaks nivo (xScale, colors, margin…) and any component that validates object shape.
  // When there is no producer example, leave those props undefined so defaults apply.
  const dataStubsCode = '{}';

  // Producer example props (F4): the curated, realistic props for the component. Build the
  // object literal by hand (not plain JSON) so props whose catalog TYPE is `Date` are emitted
  // as `new Date("...")` — examples carry ISO strings, but components like DatePicker pass the
  // value to date-fns, which throws "Invalid time value" on a string.
  const propTypeByName = {};
  props.forEach(prop => {
    propTypeByName[prop.name] = String(prop.type || '').toLowerCase();
  });
  const exampleProps =
    (Array.isArray(entry.examples) && entry.examples[0] && entry.examples[0].props) || {};
  const hasExample = Object.keys(exampleProps).length > 0;
  const examplePropsCode = `{ ${Object.entries(exampleProps)
    .map(([key, value]) => {
      if (propTypeByName[key] === 'date' && typeof value === 'string') {
        return `${JSON.stringify(key)}: new Date(${JSON.stringify(value)})`;
      }
      return `${JSON.stringify(key)}: ${JSON.stringify(value)}`;
    })
    .join(', ')} }`;
  // Render harness:
  // - Wraps the component in a container with a DEFINITE height — charts (nivo
  //   `Responsive*`) measure their parent and draw nothing when it is 0.
  // - MUI-v4-era `blocks` components need app providers (ThemeProvider + i18n). When the
  //   catalog ships a `docsProvider` (F6), that wrapper is used and provides the real Zippedi
  //   theme. Otherwise a best-effort fallback ThemeProvider is injected so components don't
  //   crash. Conditional on deps so nivo/Tremor charts are untouched.
  const deps = entry.dependencies || {};
  const usesMui = Object.keys(deps).some(dep => dep.startsWith('@mui/'));
  const usesI18n = Boolean(deps['react-i18next']);
  const usesTremor = Boolean(deps['@tremor/react']);

  // The producer ships a `docsProvider` path (F6): a DocsProvider wrapper bundled in `files`
  // that supplies the REAL Zippedi theme (palette.paper/type) + a no-op i18n so blocks render
  // (and stay interactive). Prefer it over the best-effort theme/i18n below.
  const docsProviderPath = entry.docsProvider
    ? entry.docsProvider.startsWith('/')
      ? `.${entry.docsProvider}`
      : `./${entry.docsProvider}`
    : null;

  const lines = [
    `// Auto-generated preview for "${name}".`,
    `import * as Lib from '${importPath}';`,
    `import { Component as __Component } from 'react';`,
  ];
  if (docsProviderPath) {
    lines.push(`import { DocsProvider as __DocsProvider } from '${docsProviderPath}';`);
  } else if (usesMui) {
    lines.push(
      `import { ThemeProvider as __ThemeProvider, createTheme as __createTheme } from '@mui/material';`
    );
  }
  if (!docsProviderPath && usesI18n) {
    lines.push(`import __i18n from 'i18next';`);
    lines.push(
      `import { initReactI18next as __initReactI18next, I18nextProvider as __I18nextProvider } from 'react-i18next';`
    );
  }
  lines.push('');
  if (usesTremor) {
    // Tremor needs Tailwind (sizing utilities like h-80 + tremor-* tokens). Inject the Play
    // CDN at runtime (the Sandpack runtime ignores /public/index.html).
    tremorTailwindInjectionLines().forEach(line => lines.push(line));
    lines.push('');
  }
  lines.push(`const Comp = ${candidates}.map((key) => Lib[key]).find(Boolean) || Lib.default;`);
  lines.push(`const __fnStubs = ${fnStubsCode}; // no-op callbacks (examples can't carry functions)`);
  lines.push(
    `const __dataProps = ${hasExample ? examplePropsCode : dataStubsCode}; // ${
      hasExample ? 'producer example props (F4)' : 'empty stubs — no example yet; avoids crashes'
    }`
  );
  lines.push('');
  if (!docsProviderPath && usesMui) {
    lines.push(`// Fallback theme — not the real Zippedi theme; used only when no docsProvider is present.`);
    lines.push(
      `const __theme = __createTheme({ palette: { mode: 'light', type: 'light', paper: { main: '#ffffff' } } });`
    );
  }
  if (!docsProviderPath && usesI18n) {
    lines.push(`if (!__i18n.isInitialized) {`);
    lines.push(
      `  __i18n.use(__initReactI18next).init({ lng: 'en', fallbackLng: 'en', resources: {}, interpolation: { escapeValue: false } });`
    );
    lines.push(`}`);
  }
  lines.push('');
  // Error boundary: many components require props; rendered bare (no example data yet, see
  // producer feedback F4) they may throw. Degrade gracefully to an explanatory note instead
  // of a raw crash overlay, while still surfacing the error message for debugging.
  lines.push('class __ErrorBoundary extends __Component {');
  lines.push('  constructor(props) { super(props); this.state = { error: null }; }');
  lines.push('  static getDerivedStateFromError(error) { return { error }; }');
  lines.push('  render() {');
  lines.push('    if (this.state.error) {');
  lines.push('      return (');
  lines.push("        <div style={{ padding: 16, fontFamily: 'sans-serif', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8 }}>");
  lines.push('          <strong>This component could not render without props.</strong>');
  lines.push("          <div style={{ fontSize: 12, marginTop: 6 }}>It likely needs example props (pending producer examples). Error: {String((this.state.error && this.state.error.message) || this.state.error)}</div>");
  lines.push('        </div>');
  lines.push('      );');
  lines.push('    }');
  lines.push('    return this.props.children;');
  lines.push('  }');
  lines.push('}');
  lines.push('');
  lines.push('function Preview() {');
  lines.push('  if (!Comp) {');
  lines.push(`    return (`);
  lines.push(`      <pre style={{ color: '#b00020', padding: 16, whiteSpace: 'pre-wrap' }}>`);
  lines.push(`        Could not resolve export "${name}" from ${importPath}`);
  lines.push(`      </pre>`);
  lines.push(`    );`);
  lines.push('  }');
  lines.push(`  return (`);
  // Outer padding for breathing room, then an INNER box with a DEFINITE pixel height and
  // no padding/border-box — nivo's Responsive* wrapper renders a height:100% child and needs
  // its parent's height to resolve to a clean non-zero pixel value (a padded border-box parent
  // can make it measure 0). The inner box is that clean parent.
  // Centering harness: outer flex fills the full viewport so content is always centered
  // regardless of its height; inner container gives charts (nivo Responsive*) a definite
  // height to measure via ResizeObserver while form components just take their natural size.
  lines.push(`    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 16, boxSizing: 'border-box' }}>`);
  lines.push(`      <div style={{ width: '100%', height: 440 }}>`);
  lines.push(`        <Comp {...__fnStubs} {...__dataProps} />`);
  lines.push(`      </div>`);
  lines.push(`    </div>`);
  lines.push(`  );`);
  lines.push('}');
  lines.push('');
  lines.push('export default function App() {');
  lines.push('  let node = <__ErrorBoundary><Preview /></__ErrorBoundary>;');
  if (docsProviderPath) {
    lines.push('  node = <__DocsProvider>{node}</__DocsProvider>;');
  } else {
    if (usesMui) lines.push('  node = <__ThemeProvider theme={__theme}>{node}</__ThemeProvider>;');
    if (usesI18n) lines.push('  node = <__I18nextProvider i18n={__i18n}>{node}</__I18nextProvider>;');
  }
  lines.push('  return node;');
  lines.push('}');
  lines.push('');
  const appCode = lines.join('\n');

  return {
    ...files,
    [APP_ENTRY]: appCode,
  };
}
